import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage, scrollToTop } from "helpers/common";
import { isDbModule } from "helpers/module";
import { toast } from "helpers/toast";
import { getModule } from "store/userSlice";
import { fetchBudgetSettings, getBudgetSettings } from "store/budgetSettingsSlice";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { useTranslation } from "translation/useTranslation";
import ErrorAlert from "components/ui/ErrorAlert";
import EmployeeSelector from "./EmployeeSelector";
import ScorecardInstanceEditor from "./ScorecardInstanceEditor";
import Link from "components/ui/Link";
import ActionChangePreview from "./ActionChangePreview";
import { applySelectionChange, buildWhoList, pairInstanceValues } from "./actionChangePreviewHelpers";
import { buildScorecardUpdates, hasEmptyInstanceRow, scorecardRowLabel, scorecardWeighting } from "./updateScorecardHelpers";
import { Button, Field, Select } from "components/ui";

const CONTEXT_KEYS = {
  budget: { key: 'group_name', translationKey: 'field.group', fallback: 'Gruppe' },
  group: { key: 'budget_setting_name', translationKey: 'field.budget', fallback: 'Budget' },
};

export default function UpdateScorecardAction({ action, page, setPage, setFilterCount }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const currentModule = useSelector(getModule);
  const isDbModuleActive = useMemo(() => isDbModule(currentModule), [currentModule]);

  const budgets = useSelector(getBudgetSettings);
  const groups = useSelector(getGroups);

  const targetField = action?.action_fields?.[0] === 'group' ? 'group' : 'budget';
  const options = useMemo(() => (targetField === 'group' ? groups : budgets) || [], [targetField, budgets, groups]);
  const selectOptions = useMemo(() => options.map(o => ({ value: o.hash_id, label: o.name })), [options]);

  const context = useMemo(() => {
    if (targetField === 'group' && isDbModuleActive) return null;
    return CONTEXT_KEYS[targetField];
  }, [targetField, isDbModuleActive]);

  const [actionMode, setActionMode] = useState('overwrite'); // 'overwrite' | 'per_instance'
  const [value, setValue] = useState('');
  const [instanceValues, setInstanceValues] = useState({}); // hash_id -> value hash id

  const [rows, setRows] = useState([]);
  const [loadingPool, setLoadingPool] = useState(true);
  const [poolLoaded, setPoolLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selectedRows, setSelectedRows] = useState({}); // hash_id -> display data for the step-2 who list
  const [submitting, setSubmitting] = useState(false);

  const weightingLabel = translate('field.weighting', 'Gewichtung');

  const [poolPage, setPoolPage] = useState(1);
  const [poolPerPage, setPoolPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [poolSearch, setPoolSearch] = useState('');
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, perPage: 20, currentPage: 1 });

  // Switching overwrite <-> per-instance starts the employee list fresh (page 1, no search).
  const changeActionMode = (mode) => {
    if (mode === actionMode) return;
    setActionMode(mode);
    setSearchInput('');
    setPoolSearch('');
    setPoolPage(1);
  };

  useEffect(() => {
    if (targetField === 'group') {
      if (!groups) dispatch(fetchGroups());
    } else if (!budgets) {
      dispatch(fetchBudgetSettings());
    }
  }, [dispatch, targetField, budgets, groups]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPoolSearch(searchInput.trim());
      setPoolPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPoolPage(1);
  }, [action]);

  useEffect(() => {
    let active = true;

    const fetchPool = async () => {
      setLoadingPool(true);
      setError(null);

      try {
        const body = {
          missing: targetField,
          filters: action?.filters || [],
          limit: poolPerPage,
          page: poolPage,
        };
        if (poolSearch) body.search = poolSearch;

        const { data, status } = await api.post('/employee-balanced-scorecards/search', body);
        if (!active) return;

        if (status === 200) {
          const list = (data?.data || []).map(item => {
            const weighting = scorecardWeighting(item);
            return {
              ...item,
              label: scorecardRowLabel(item),
              tooltip: weighting ? `${weightingLabel}: ${weighting}%` : null,
            };
          });
          setRows(list);

          const p = data?.meta?.pagination;
          setPagination({
            total: p?.total ?? list.length,
            totalPages: p?.total_pages ?? 1,
            perPage: p?.per_page ?? poolPerPage,
            currentPage: p?.current_page ?? poolPage,
          });
          if (!poolSearch && setFilterCount) setFilterCount(p?.total ?? list.length);
        }
      } catch (err) {
        if (!active) return;
        setError(errorMessage(err, 'fetch balanced scorecards for action'));
        Sentry.captureException(err);
      } finally {
        if (active) {
          setLoadingPool(false);
          setPoolLoaded(true);
        }
      }
    };

    fetchPool();

    return () => { active = false; };
  }, [action, targetField, poolPage, poolPerPage, poolSearch, setFilterCount, weightingLabel]);

  const handlePoolPerPageChange = (n) => {
    setPoolPerPage(n);
    setPoolPage(1);
  };

  const handleSelectionChange = (changed, checked) => setSelectedRows(prev => applySelectionChange(prev, changed, checked));

  const poolPaginationProps = {
    search: searchInput,
    onSearchChange: setSearchInput,
    total: pagination.total,
    loadingPool,
    page: pagination.currentPage,
    perPage: pagination.perPage,
    totalPages: pagination.totalPages,
    totalRows: pagination.total,
    setPage: setPoolPage,
    setPerPage: handlePoolPerPageChange,
  };

  const fieldLabel = useMemo(() => (
    targetField === 'group'
      ? editableTranslate('field.group', 'Gruppe')
      : editableTranslate('field.budget', 'Budget')
  ), [targetField, editableTranslate]);

  const listTitle = isDbModuleActive
    ? editableTranslate('action.detail.evaluations', 'Beurteilungen')
    : editableTranslate('action.detail.balanced_scorecards', 'Leistungskarten');

  const listIntro = isDbModuleActive
    ? editableTranslate('action.detail.select_evaluations', 'Wählen Sie die Beurteilungen aus, auf die die Änderungen angewendet werden sollen.')
    : editableTranslate('action.detail.select_balanced_scorecards', 'Wählen Sie die Leistungskarten aus, auf die die Änderungen angewendet werden sollen.');

  const instanceIntro = isDbModuleActive
    ? editableTranslate('action.detail.fill_per_evaluation_text', 'Wählen Sie Beurteilungen aus und füllen Sie das Feld aus.')
    : editableTranslate('action.detail.fill_per_scorecard_text', 'Wählen Sie Leistungskarten aus und füllen Sie das Feld aus.');

  const tabLabel = targetField === 'group'
    ? editableTranslate('action.detail.assign_group', 'Gruppe zuweisen')
    : editableTranslate('action.detail.assign_budget', 'Budget zuweisen');

  const previewTitle = isDbModuleActive
    ? editableTranslate('action.detail.preview.selected_evaluations', 'Ausgewählte Beurteilungen')
    : editableTranslate('action.detail.preview.selected_balanced_scorecards', 'Ausgewählte Leistungskarten');

  const previewCountTemplate = isDbModuleActive
    ? editableTranslateString('action.detail.preview.who_count_evaluations', '$X Beurteilungen')
    : editableTranslateString('action.detail.preview.who_count_balanced_scorecards', '$X Leistungskarten');

  const optionName = (hashId) => options.find(o => o.hash_id === hashId)?.name || '';

  const updates = useMemo(
    () => buildScorecardUpdates(selected, actionMode, value, instanceValues),
    [selected, actionMode, value, instanceValues]
  );

  const canProceed = poolLoaded && updates.length > 0;

  const goToReview = () => {
    if (hasEmptyInstanceRow(selected, actionMode, instanceValues)) {
      setError(errorMessage(translate('action.detail.instance.fill_required', 'Bitte füllen Sie für jede ausgewählte Person mindestens ein Feld aus.'), 'update scorecard'));
      scrollToTop();
      return;
    }
    setError(null);
    setPage(2);
  };

  const handleApply = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const { status } = await api.post('/employee-balanced-scorecards/bulk-update', {
        field: targetField,
        updates,
      });
      if (status === 200) {
        toast.success(translate('form.success.saved'));
        navigate('/settings/actions');
      }
    } catch (err) {
      setError(errorMessage(err, 'apply update_balanced_scorecard action'));
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3">
      <hr className="mt-2 mb-4" />
      {
        error &&
        <div className="mb-3">
          <ErrorAlert error={error} onClose={() => setError(null)} isHtml={true} />
        </div>
      }

      {
        page === 1 && <>
          {
            actionMode === 'overwrite' && <div className="mb-5">
              <div className="font-weight-bold">{listTitle}</div>
              <hr className="my-2" />
              <div className="mb-3">{listIntro}</div>

              {
                poolLoaded && <EmployeeSelector
                  employees={rows}
                  selected={selected}
                  setSelected={setSelected}
                  onSelectionChange={handleSelectionChange}
                  {...poolPaginationProps}
                />
              }
            </div>
          }

          {
            !poolLoaded && <div className="text-center p-4" data-testid="loading">
              <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
            </div>
          }

          <div className="mb-3">
            <div className="action-tab-mode-div">
              <div className="flex-1 border-bottom">
                <div className="employee-management-tabs">
                  <div className="cursor-pointer active">
                    <i className="bi bi-person-square mr-1"></i>
                    <span>{tabLabel}</span>
                  </div>
                </div>
              </div>
              <div className="btn-group btn-group-switch-mode normal-border padding-normal" role="group">
                <button type="button" className={`btn ${actionMode === 'overwrite' ? ' active' : ''}`} onClick={() => changeActionMode('overwrite')} data-testid="mode-overwrite-button">
                  {editableTranslate('action.detail.mode.overwrite', 'Überschreiben')}
                </button>
                <button type="button" className={`btn ${actionMode === 'per_instance' ? ' active' : ''}`} onClick={() => changeActionMode('per_instance')} data-testid="mode-per-instance-button">
                  {
                    isDbModuleActive
                      ? editableTranslate('action.detail.mode.per_instance_evaluation', 'Pro Beurteilung ausfüllen')
                      : editableTranslate('action.detail.mode.per_instance_scorecard', 'Pro Leistungskarte ausfüllen')
                  }
                </button>
              </div>
            </div>
          </div>

          {
            actionMode === 'overwrite' && <div className="form-group mb-4">
              <Field label={fieldLabel}>
                <div className="col-md-4 p-0">
                  <div style={{ display: 'contents' }} data-testid="scorecard-value-select">
                    <Select
                      options={selectOptions}
                      value={selectOptions.find(o => o.value === value) || null}
                      onChange={option => setValue(option ? option.value : '')}
                      isClearable
                      placeholder={translate('form.select_placeholder')}
                    />
                  </div>
                </div>
              </Field>
            </div>
          }

          <div>
            {
              (poolLoaded && actionMode !== 'overwrite') && <ScorecardInstanceEditor
                rows={rows}
                options={options}
                intro={instanceIntro}
                fieldLabel={fieldLabel}
                contextKey={context?.key || null}
                contextLabel={context ? editableTranslate(context.translationKey, context.fallback) : ''}
                values={instanceValues}
                onValueChange={(hashId, next) => setInstanceValues(prev => ({ ...prev, [hashId]: next }))}
                selected={selected}
                setSelected={setSelected}
                onSelectionChange={handleSelectionChange}
                {...poolPaginationProps}
              />
            }
          </div>

          <div className="settings-sticky-footer mt-3">
            <Button data-testid="back-button" as={Link} variant="secondary" className="mr-2" to="/settings/actions">
              {editableTranslate('button.back', 'Zurück')}
            </Button>

            <Button variant="primary" onClick={goToReview} disabled={!canProceed} data-testid="next-button">
              {editableTranslate('button.next', 'Weiter')}
            </Button>
          </div>
        </>
      }

      {
        page === 2 && <>
          <div>
            {
              actionMode === 'overwrite'
                ? <ActionChangePreview
                  whatItems={[{ label: fieldLabel, value: optionName(value) }]}
                  whoList={buildWhoList(updates.map(u => u.hash_id), selectedRows)}
                  whoTitle={previewTitle}
                  whoCountTemplate={previewCountTemplate}
                />
                : <ActionChangePreview
                  perInstance={true}
                  whoTitle={previewTitle}
                  whoCountTemplate={previewCountTemplate}
                  whoList={pairInstanceValues(
                    updates.map(u => u.hash_id),
                    selectedRows,
                    (hashId) => [{ label: fieldLabel, value: optionName(instanceValues[hashId]) }]
                  )}
                />
            }
          </div>

          <div className="settings-sticky-footer d-flex mt-3">
            <Button variant="secondary" onClick={() => setPage(1)} disabled={submitting} data-testid="back-button" className="mr-2">
              {editableTranslate('button.back', 'Zurück')}
            </Button>
            <Button variant="primary" onClick={handleApply} loading={submitting} disabled={updates.length === 0} data-testid="proceed-button">{editableTranslate('button.proceed')}</Button>
          </div>
        </>
      }
    </div>
  );
}
